// @ts-nocheck -- Transitional typing for a legacy JavaScript module.
import { useMemo, useState } from "react";
import { endOfDay, startOfDay } from "date-fns";
import { History } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import DateRangeFilter from "@/components/common/DateRangeFilter";
import ExportButton from "@/components/common/ExportButton";
import EmptyState from "@/components/common/EmptyState";
import SearchField from "@/components/common/SearchField";
import UserAvatar from "@/components/common/UserAvatar";
import { downloadCsv } from "@/utils/common.utils";
import { cn } from "@/utils/common.utils";
import SectionCard from "@/components/common/SectionCard";
import { formatActivityTimestamp } from "@/utils/deals-view.utils";
import EmailLogList from "@/components/deals/tabs/EmailLogList";

export default function ActivityTab({ activity, deal }) {
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState();
  const [openId, setOpenId] = useState(null);
  const [view, setView] = useState("activity");

  const viewSwitch = (
    <div className="mb-5 flex w-fit items-center gap-1 rounded-lg border border-dash-border bg-white p-1">
      <Button
        variant="seamless"
        type="button"
        onClick={() => setView("activity")}
        data-testid="activity-view-activity"
        aria-pressed={view === "activity"}
        className={cn(
          "h-8 rounded-md px-3 text-xs font-medium transition-colors",
          view === "activity" ? "bg-primary text-white hover:text-white" : "text-dash-muted hover:bg-dash-searchBg hover:text-dash-ink"
        )}
      >
        Activity
      </Button>
      <Button
        variant="seamless"
        type="button"
        onClick={() => setView("emails")}
        data-testid="activity-view-emails"
        aria-pressed={view === "emails"}
        className={cn(
          "h-8 rounded-md px-3 text-xs font-medium transition-colors",
          view === "emails" ? "bg-primary text-white hover:text-white" : "text-dash-muted hover:bg-dash-searchBg hover:text-dash-ink"
        )}
      >
        Emails
      </Button>
    </div>
  );

  const visibleActivity = useMemo(() => {
    const query = search.trim().toLowerCase();
    const from = dateRange?.from ? startOfDay(dateRange.from).getTime() : null;
    const to = dateRange?.from ? endOfDay(dateRange.to || dateRange.from).getTime() : null;
    return activity.filter((item) => {
      const searchable = [item.user_name, item.user_role, item.message].filter(Boolean).join(" ").toLowerCase();
      const timestamp = new Date(item.created_at).getTime();
      return (!query || searchable.includes(query)) && (!from || (timestamp >= from && timestamp <= to));
    });
  }, [activity, search, dateRange]);

  const exportActivity = () => {
    if (!visibleActivity.length) {
      toast.info("There is no activity to export");
      return;
    }
    downloadCsv({
      filename: `activity-${deal.id || "deal"}.csv`,
      rows: [
        ["Date", "Person", "Role", "Activity"],
        ...visibleActivity.map((item) => [item.created_at, item.user_name, item.user_role, item.message]),
      ],
    });
  };

  if (view === "emails") {
    return (
      <section data-testid="activity-tab">
        {viewSwitch}
        <EmailLogList dealId={deal.id} />
      </section>
    );
  }

  return (
    <section data-testid="activity-tab">
      {viewSwitch}
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <SearchField value={search} onChange={(event) => setSearch(event.target.value)} onClear={() => setSearch("")} placeholder="Search activity..." className="w-full sm:w-[276px]" testId="activity-search" />
        <DateRangeFilter value={dateRange} onChange={setDateRange} numberOfMonths={1} className="w-full sm:w-auto" testId="activity-date-range" />
        <ExportButton
          onExport={exportActivity}
          className="h-11 w-full !rounded-xl sm:ml-auto sm:w-auto"
          testId="activity-export"
        />
      </div>

      {!visibleActivity.length ? (
        <EmptyState icon={History} title="No activity found" description={activity.length ? "Try changing the search term or selected date range." : "Deal updates will appear here as your team starts working."} />
      ) : (
        <SectionCard className="overflow-hidden" contentClassName="p-0">
        <ul>
          {visibleActivity.map((item, index) => {
            const open = openId === item.id;
            return (
              <li key={item.id} className={cn(index > 0 && "border-t border-dash-border")}>
                <div className="flex min-h-[86px] items-start gap-3 px-4 py-4 sm:items-center sm:gap-4 sm:px-6">
                  <UserAvatar name={item.user_name || "System"} src={item.photo_url} className="size-9 shrink-0" fallbackClassName="bg-primary text-primary-foreground" />
                  <div className="min-w-0 flex-1">
                    <p className="break-words text-sm text-dash-ink"><span className="font-semibold">{item.user_name || "System"}</span> {item.message}</p>
                    <p className="mt-1 text-xs text-dash-faint">{formatActivityTimestamp(item.created_at)}</p>
                  </div>
                  <Button variant="seamless" type="button" onClick={() => setOpenId(open ? null : item.id)} className="shrink-0 pt-1 text-xs text-dash-faint hover:text-dash-ink sm:pt-0" aria-expanded={open} data-testid={`activity-details-${item.id}`}>Details</Button>
                </div>
                {open && (
                  <div className="break-all border-t border-dash-border bg-dash-searchBg px-4 py-3 text-xs text-dash-muted sm:px-[5.25rem]">
                    <span className="font-medium text-dash-ink">Role:</span> {(item.user_role || "system").replaceAll("_", " ")}
                    <span className="mx-2">·</span>
                    <span className="font-medium text-dash-ink">Activity ID:</span> {item.id}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        </SectionCard>
      )}
    </section>
  );
}
